import { AsyncPipe, NgClass, NgTemplateOutlet } from '@angular/common';
import {
  type AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  effect,
  ElementRef,
  EventEmitter,
  input,
  Input,
  model,
  type OnDestroy,
  Output,
  ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import type {
  FilterItem,
  ResultSuggestionItem,
  SearchSuggestionGroup,
  SmartSuggestionItem,
} from '@bizzdesign/data-access-search';
import { SharedI18nModule } from '@bizzdesign/shared/i18n';
import {
  HighlightTextPipe,
  SanitizePipe,
  TranslateDisplayTextPipe,
} from '@bizzdesign/shared/pipes';
import {
  type AutoComplete,
  type AutoCompleteCompleteEvent,
  AutoCompleteModule,
} from 'primeng/autocomplete';
import { ButtonModule } from 'primeng/button';
import { DividerModule } from 'primeng/divider';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { SkeletonModule } from 'primeng/skeleton';
import { TooltipModule } from 'primeng/tooltip';

@Component({
  selector: 'bd-searchbar',
  imports: [
    AutoCompleteModule,
    ButtonModule,
    DividerModule,
    FormsModule,
    HighlightTextPipe,
    IconFieldModule,
    InputIconModule,
    SharedI18nModule,
    SkeletonModule,
    TooltipModule,
    NgClass,
    NgTemplateOutlet,
    AsyncPipe,
    SanitizePipe,
    TranslateDisplayTextPipe,
  ],
  templateUrl: './searchbar.component.html',
  styleUrls: ['./searchbar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SearchbarComponent implements AfterViewInit, OnDestroy {
  @ViewChild('autocomplete') autocomplete?: AutoComplete;
  @ViewChild('seeAllButton', { read: ElementRef })
  seeAllButton?: ElementRef<HTMLElement>;
  allowSuggestionOpen = true;
  private readonly hiddenSuggestions: SearchSuggestionGroup[] = [];

  @Input() disabled = false;
  private _combinedSuggestions: SearchSuggestionGroup[] = [];
  @Input() set combinedSuggestions(value: SearchSuggestionGroup[]) {
    this._combinedSuggestions = value;
    this.autocomplete?.focusedOptionIndex.set(-1);
  }
  get combinedSuggestions(): SearchSuggestionGroup[] {
    return this._combinedSuggestions;
  }
  @Input() searchTerm: string = '';
  @Input() set context(context: string) {
    this._context = context;
    if (context !== 'search' && this.searchTerm !== '') {
      this.clearInput();
    }
  }
  get context(): string {
    return this._context;
  }
  aiSearchEnabled = input<boolean>(false);
  aiSearchToggleOn = input<boolean>(false);

  private _focusInput: boolean = false;
  @Input()
  set focusInput(value: boolean) {
    this._focusInput = value;
    if (value) {
      this.autocomplete?.inputEL?.nativeElement.focus();
    }
  }
  get focusInput(): boolean {
    return this._focusInput;
  }

  @Output() submitSearch: EventEmitter<string> = new EventEmitter();
  @Output() clearSearchInput: EventEmitter<string> = new EventEmitter();
  @Output() aiSearchChange: EventEmitter<boolean> = new EventEmitter();
  @Output() submitTypeAheadSearch: EventEmitter<string> = new EventEmitter();
  @Output() submitSuggestionFilter: EventEmitter<FilterItem> =
    new EventEmitter();
  @Output() submitSmartSuggestion: EventEmitter<SmartSuggestionItem> =
    new EventEmitter();
  @Output() submitObjectSuggestion: EventEmitter<ResultSuggestionItem> =
    new EventEmitter();

  private _context: string = 'search';
  toggleState = model<boolean>(false);

  constructor() {
    effect(() => {
      this.toggleState.set(this.aiSearchToggleOn());
    });
  }

  ngAfterViewInit(): void {
    this.autocomplete?.el?.nativeElement.addEventListener(
      'keydown',
      this.handleArrowDownToSeeAll,
      true,
    );
  }

  ngOnDestroy(): void {
    this.autocomplete?.el?.nativeElement.removeEventListener(
      'keydown',
      this.handleArrowDownToSeeAll,
      true,
    );
  }

  private readonly handleArrowDownToSeeAll = (event: KeyboardEvent): void => {
    if (
      event.key !== 'ArrowDown' ||
      !this.autocomplete ||
      !this.canFocusSeeAllButton()
    ) {
      return;
    }

    const lastIndex = this.autocomplete.findLastOptionIndex();
    const focusedIndex = this.autocomplete.focusedOptionIndex();

    if (lastIndex !== -1 && focusedIndex === lastIndex) {
      event.preventDefault();
      event.stopPropagation();
      this.focusSeeAllButton();
    }
  };

  private canFocusSeeAllButton(): boolean {
    return !this.aiSearchToggleOn() && !!this.searchTerm;
  }

  private focusSeeAllButton(): void {
    this.seeAllButton?.nativeElement
      .querySelector<HTMLButtonElement>('button')
      ?.focus();
  }

  get visibleSuggestions(): SearchSuggestionGroup[] {
    return this.allowSuggestionOpen
      ? this.combinedSuggestions
      : this.hiddenSuggestions;
  }

  submitInput(selectedTerm?: string) {
    if (selectedTerm) {
      this.searchTerm = selectedTerm;
    }
    this.submitSearch.emit(this.searchTerm);
  }

  onInputFocus() {
    this.allowSuggestionOpen = true;
    this.search();
  }

  onInputClick() {
    this.allowSuggestionOpen = true;
    this.search();
  }

  onSearchButtonMouseDown(event: MouseEvent): void {
    // Prevent p-autocomplete host click from re-focusing input.
    this.autocomplete?.hide();
    event.preventDefault();
    event.stopPropagation();
  }

  onSearchButtonClick(event: Event): void {
    this.submitAndCloseSuggestions(event);
  }

  onInputEnterKeyDown(event: Event): void {
    this.submitAndCloseSuggestions(event);
  }

  onControlsContainerMouseDown(event: MouseEvent): void {
    event.stopPropagation();
  }

  clearInput(emitClearSearch = true) {
    if (emitClearSearch) {
      this.searchTerm = '';
      this.clearSearchInput.emit();
    } else {
      this.submitTypeAheadSearch.emit('');
    }
  }

  onToggle() {
    this.toggleState.set(!this.toggleState());
    this.aiSearchChange.emit(this.toggleState());
  }

  onSuggestionSelect(suggestion: ResultSuggestionItem | SmartSuggestionItem) {
    if (suggestion.type === 'query') {
      this.searchTerm = suggestion.query ?? '';
      this.submitSmartSuggestion.emit(suggestion);
    } else if (typeof suggestion.name === 'string') {
      this.searchTerm = suggestion.name;
      this.submitObjectSuggestion.emit(suggestion);
    }

    this.autocomplete?.hide();
  }

  onFilterSuggestionClick(
    filterSuggestion: FilterItem,
    event: Event,
    selected: boolean,
  ): void {
    event.stopPropagation();
    filterSuggestion.selected = selected;
    this.submitSuggestionFilter.emit(filterSuggestion);
  }

  onInputChange(event: Event) {
    const target = event.target as HTMLInputElement;
    this.allowSuggestionOpen = true;
    this.searchTerm = target.value;
    this.submitTypeAheadSearch.emit(this.searchTerm);
  }

  search(event?: AutoCompleteCompleteEvent) {
    if (!this.allowSuggestionOpen) {
      // While search submission is in progress, ignore any late completeMethod
      // callbacks so the autocomplete overlay cannot reopen itself.
      return;
    }

    this.autocomplete?.show();

    if (event) {
      this.searchTerm = event.query;
    }
    this.submitTypeAheadSearch.emit(this.searchTerm);
  }

  private closeSuggestionsAfterSubmit(): void {
    this.allowSuggestionOpen = false;
    this.autocomplete?.hide();
  }

  private submitAndCloseSuggestions(event: Event): void {
    event.stopPropagation();
    this.closeSuggestionsAfterSubmit();
    this.submitInput();
  }

  onAutoCompleteHide(): void {
    if (
      this.allowSuggestionOpen &&
      this.autocomplete?.focused &&
      this.searchTerm === ''
    ) {
      this.autocomplete?.show();
    }
  }
}
